// src/components/admin/CampaignsListingEditor.jsx - FIXED WITH DELIVERY TRACKING
import React, { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import whatsappService from '../../api/whatsappService';
import '../../styles/reports.css';

function CampaignsListingEditor({ salonId, onUpdate, onEditCampaign }) {
  const { currentUser } = useSalonAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [viewingRecipients, setViewingRecipients] = useState(null);

  // Filters
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Load notifications from mail, pushNotifications, and whatsappMessages collections
  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Loading campaigns for salon:', salonId);

      // Query email, push, and WhatsApp collections
      const [emailQuery, pushQuery, whatsappQuery] = await Promise.all([
        getDocs(query(
          collection(db, 'mail'),
          where('salonId', '==', salonId),
          where('metadata.campaignType', '==', 'actual_blast')
        )),
        getDocs(query(
          collection(db, 'pushNotifications'),
          where('salonId', '==', salonId),
          where('metadata.campaignType', '==', 'actual_blast')
        )),
        getDocs(query(
          collection(db, 'whatsappMessages'),
          where('salonId', '==', salonId),
          where('metadata.campaignType', '==', 'actual_blast')
        ))
      ]);

      // Process email campaigns — collect raw docs first, then consolidate
      const rawEmailDocs = [];
      emailQuery.forEach((docSnap) => {
        const data = docSnap.data();
        rawEmailDocs.push({
          id: docSnap.id,
          ...data,
          campaignType: 'email',
          displayType: 'Email Campaign',
          scheduledDateTime: data.metadata?.scheduledDate && data.metadata?.scheduledTime ?
            new Date(`${data.metadata.scheduledDate}T${data.metadata.scheduledTime}`) :
            new Date(),
          title: data.subject,
          subject: data.subject,
          body: data.text || data.html || '',
          targetingType: data.metadata?.targetingType || 'all_customers',
          targetingCriteria: data.metadata?.targetingCriteria || {},
          scheduledDate: data.metadata?.scheduledDate || '',
          scheduledTime: data.metadata?.scheduledTime || '',
          deliveryState: data.delivery?.state || data.state || null,
          deliveryInfo: data.delivery?.info || null,
          deliveryAttempts: data.delivery?.attempts || null,
          deliveryEndTime: data.delivery?.endTime || null,
          deliveryStartTime: data.delivery?.startTime || null
        });
      });

      // Consolidate personalized email docs (multiple docs per campaign) into single entries
      const consolidatedEmails = consolidateEmailCampaigns(rawEmailDocs);

      const campaignsList = [...consolidatedEmails];

      // Process push notifications
      pushQuery.forEach((docSnap) => {
        const data = docSnap.data();
        campaignsList.push({
          id: docSnap.id,
          ...data,
          campaignType: 'push',
          displayType: 'Push Notification',
          scheduledDateTime: data.metadata?.scheduledDate && data.metadata?.scheduledTime ?
            new Date(`${data.metadata.scheduledDate}T${data.metadata.scheduledTime}`) :
            new Date(),
          subject: data.title,
          title: data.title,
          body: data.body || '',
          targetingType: data.metadata?.targetingType || 'all_customers',
          targetingCriteria: data.metadata?.targetingCriteria || {},
          scheduledDate: data.metadata?.scheduledDate || '',
          scheduledTime: data.metadata?.scheduledTime || '',
          deliveryState: data.delivery?.state || data.state || null,
          deliveryInfo: data.delivery?.info || null
        });
      });

      // Process WhatsApp campaigns
      whatsappQuery.forEach((docSnap) => {
        const data = docSnap.data();
        campaignsList.push({
          id: docSnap.id,
          ...data,
          campaignType: 'whatsapp',
          displayType: 'WhatsApp Campaign',
          scheduledDateTime: data.metadata?.scheduledDate && data.metadata?.scheduledTime ?
            new Date(`${data.metadata.scheduledDate}T${data.metadata.scheduledTime}`) :
            new Date(),
          subject: data.title,
          title: data.title,
          body: data.body || '',
          targetingType: data.metadata?.targetingType || 'all_customers',
          targetingCriteria: data.metadata?.targetingCriteria || {},
          scheduledDate: data.metadata?.scheduledDate || '',
          scheduledTime: data.metadata?.scheduledTime || '',
          deliveryState: data.delivery?.state || data.state || null,
          deliveryInfo: data.delivery?.info || null
        });
      });

      // Sort campaigns by creation date (newest first)
      campaignsList.sort((a, b) => {
        const dateA = new Date(a.metadata?.timestamp || a.scheduledDateTime);
        const dateB = new Date(b.metadata?.timestamp || b.scheduledDateTime);
        return dateB - dateA;
      });

      console.log(`Successfully loaded ${campaignsList.length} actual_blast campaigns:`, campaignsList);
      setNotifications(campaignsList);

    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(`Failed to load campaigns: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Consolidate personalized email docs into single campaign entries
  const consolidateEmailCampaigns = (rawDocs) => {
    const campaignGroups = {};

    rawDocs.forEach(emailDoc => {
      // Primary: group by campaignBatchId (new campaigns have this)
      // Fallback: group by scheduledDate + scheduledTime + targetingType + createdBy (older campaigns)
      let groupKey;
      if (emailDoc.metadata?.campaignBatchId) {
        groupKey = emailDoc.metadata.campaignBatchId;
      } else {
        groupKey = `${emailDoc.metadata?.scheduledDate || ''}_${emailDoc.metadata?.scheduledTime || ''}_${emailDoc.metadata?.targetingType || ''}_${emailDoc.metadata?.createdBy || ''}_${emailDoc.metadata?.timestamp?.substring(0, 16) || ''}`;
      }

      if (!campaignGroups[groupKey]) {
        campaignGroups[groupKey] = {
          ...emailDoc,
          // Ensure `to` is an array and collect all recipients
          _allRecipients: [...(emailDoc.to || [])],
          _allDocIds: [emailDoc.id],
          _deliveryStates: emailDoc.deliveryState ? [emailDoc.deliveryState] : [],
          _deliveryAttempts: emailDoc.deliveryAttempts ? [...emailDoc.deliveryAttempts] : [],
          _totalOpens: emailDoc.analytics?.openedBy?.length ?? emailDoc.analytics?.opens ?? 0,
          _totalClicks: emailDoc.analytics?.clickedBy?.length ?? emailDoc.analytics?.clicks ?? 0,
          _totalUnsubscribes: emailDoc.analytics?.unsubscribedBy?.length ?? emailDoc.analytics?.unsubscribes ?? 0,
          _totalSpamReports: emailDoc.analytics?.spamBy?.length ?? emailDoc.analytics?.spamReports ?? 0,
          _hasAnalytics: (emailDoc.analytics?.openedBy != null || emailDoc.analytics?.opens != null || emailDoc.analytics?.clickedBy != null || emailDoc.analytics?.clicks != null || emailDoc.analytics?.unsubscribedBy != null || emailDoc.analytics?.unsubscribes != null || emailDoc.analytics?.spamBy != null || emailDoc.analytics?.spamReports != null),
        };
      } else {
        const group = campaignGroups[groupKey];
        // Merge recipients
        if (emailDoc.to) {
          group._allRecipients.push(...emailDoc.to);
        }
        group._allDocIds.push(emailDoc.id);
        if (emailDoc.deliveryState) {
          group._deliveryStates.push(emailDoc.deliveryState);
        }
        if (emailDoc.deliveryAttempts) {
          group._deliveryAttempts.push(...emailDoc.deliveryAttempts);
        }
        group._totalOpens += emailDoc.analytics?.openedBy?.length ?? emailDoc.analytics?.opens ?? 0;
        group._totalClicks += emailDoc.analytics?.clickedBy?.length ?? emailDoc.analytics?.clicks ?? 0;
        group._totalUnsubscribes += emailDoc.analytics?.unsubscribedBy?.length ?? emailDoc.analytics?.unsubscribes ?? 0;
        group._totalSpamReports += emailDoc.analytics?.spamBy?.length ?? emailDoc.analytics?.spamReports ?? 0;
        if (emailDoc.analytics?.openedBy != null || emailDoc.analytics?.opens != null || emailDoc.analytics?.clickedBy != null || emailDoc.analytics?.clicks != null || emailDoc.analytics?.unsubscribedBy != null || emailDoc.analytics?.unsubscribes != null || emailDoc.analytics?.spamBy != null || emailDoc.analytics?.spamReports != null) {
          group._hasAnalytics = true;
        }
      }
    });

    // Convert groups back to campaign objects
    return Object.values(campaignGroups).map(group => {
      const allRecipients = [...new Set(group._allRecipients)]; // deduplicate
      const successCount = group._deliveryStates.filter(s => s === 'SUCCESS').length;
      const totalDocs = group._allDocIds.length;

      // Determine consolidated delivery state
      let consolidatedState = null;
      if (totalDocs > 0) {
        if (successCount === totalDocs) {
          consolidatedState = 'SUCCESS';
        } else if (successCount > 0) {
          consolidatedState = 'SUCCESS'; // At least some delivered
        } else if (group._deliveryStates.some(s => s === 'ERROR')) {
          consolidatedState = 'ERROR';
        }
      }

      // Use originalSubject (template) if available, otherwise first doc's subject
      const displaySubject = group.metadata?.originalSubject || group.subject || group.title || '';

      return {
        ...group,
        subject: displaySubject,
        title: displaySubject,
        to: allRecipients,
        deliveryState: consolidatedState,
        deliveryAttempts: group._deliveryAttempts.length > 0 ? group._deliveryAttempts : null,
        analytics: group._hasAnalytics ? {
          opens: group._totalOpens,
          clicks: group._totalClicks,
          unsubscribes: group._totalUnsubscribes,
          spamReports: group._totalSpamReports,
        } : null,
        _consolidatedDocIds: group._allDocIds,
        _consolidatedTotal: totalDocs,
        _consolidatedSuccessCount: successCount
      };
    });
  };

  // Load notifications when component mounts
  useEffect(() => {
    if (salonId) {
      loadNotifications();
    }
  }, [salonId]);

  // Handle edit campaign
  const handleEditCampaign = (campaign) => {
    console.log('Editing campaign:', campaign);

    const editData = {
      id: campaign.id,
      type: campaign.campaignType,
      subject: campaign.subject || campaign.title || '',
      body: campaign.body || '',
      // Clear schedule so user must pick a new send time
      scheduledDate: '',
      scheduledTime: '',
      schedulingType: campaign.schedulingType || campaign.metadata?.schedulingType || 'one-time',
      recurring: campaign.recurring || campaign.metadata?.recurring || null,
      targetingType: campaign.targetingType || 'all_customers',
      targetingCriteria: campaign.targetingCriteria || {
        birthMonths: [],
        postalCodes: [],
        lastTransactionMonths: 3,
        transactionCount: 2,
        transactionType: 'less_than',
        transactionAmountType: 'greater_than',
        transactionAmount: 0,
        memberTiers: [],
        pointsType: 'greater_than',
        pointsAmount: 0
      },
      // Push / WhatsApp image
      imageUrl: campaign.imageUrl || '',
      // WhatsApp-specific
      messageCategory: campaign.messageCategory || 'not_selected',
      messageType: campaign.messageType || 'not_selected',
    };

    if (onEditCampaign) {
      onEditCampaign(campaign.campaignType, editData);
    }
  };

  // Delete campaign functionality
  const handleDeleteCampaign = async (campaignId, campaignType) => {
    if (window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      try {
        setSaving(true);
        setError('');

        const collectionMap = {
          push: 'pushNotifications',
          whatsapp: 'whatsappMessages',
          email: 'mail'
        };
        const collectionName = collectionMap[campaignType] || 'mail';
        await deleteDoc(doc(db, collectionName, campaignId));

        setSuccess('Campaign deleted successfully');
        setTimeout(() => setSuccess(''), 3000);

        loadNotifications();

        if (onUpdate) {
          onUpdate();
        }

      } catch (err) {
        console.error('Error deleting campaign:', err);
        setError('Failed to delete campaign: ' + err.message);
      } finally {
        setSaving(false);
      }
    }
  };

  // Send now functionality
  const handleSendNow = async (campaignId, campaignType) => {
    if (window.confirm('Are you sure you want to send this campaign immediately?')) {
      try {
        setSaving(true);
        setError('');

        if (campaignType === 'whatsapp') {
          // WhatsApp: call backend directly — it reads the campaign, sends via Meta API,
          // and updates the Firestore status to 'sent' itself
          await whatsappService.sendCampaign(campaignId, salonId);
          setSuccess('WhatsApp campaign sent successfully!');
        } else {
          // Email / push: flag Firestore for background function to pick up
          const collectionMap = {
            push: 'pushNotifications',
            email: 'mail'
          };
          const collectionName = collectionMap[campaignType] || 'mail';
          await updateDoc(doc(db, collectionName, campaignId), {
            'metadata.sendNow': true,
            'metadata.updatedAt': new Date().toISOString(),
            status: 'sending'
          });
          setSuccess('Campaign marked for immediate sending');
        }

        setTimeout(() => setSuccess(''), 3000);
        loadNotifications();

        if (onUpdate) {
          onUpdate();
        }

      } catch (err) {
        console.error('Error sending campaign now:', err);
        setError('Failed to send campaign: ' + err.message);
      } finally {
        setSaving(false);
      }
    }
  };

  // View recipients functionality
  const handleViewRecipients = (campaign) => {
    setViewingRecipients(campaign);
  };

  const closeRecipientsModal = () => {
    setViewingRecipients(null);
  };

  // Get successfully delivered emails only
  const getSuccessfullyDeliveredEmails = (campaign) => {
    if (campaign.campaignType === 'email') {
      if (campaign.deliveryAttempts && Array.isArray(campaign.deliveryAttempts)) {
        const successfulEmails = campaign.deliveryAttempts
          .filter(attempt => attempt.state === 'SUCCESS')
          .map(attempt => attempt.recipient || attempt.to || '')
          .filter(email => email);
        return successfulEmails;
      }
      if (campaign.deliveryState === 'SUCCESS') {
        return campaign.to || [];
      }
      return [];
    } else if (campaign.campaignType === 'push' || campaign.campaignType === 'whatsapp') {
      return (campaign.targetRecipients || []).map(recipient => {
        if (typeof recipient === 'string') return recipient;
        return recipient.email || recipient.phoneNumber || '';
      }).filter(val => val);
    }
    return [];
  };

  // Get delivery statistics for email campaigns
  const getDeliveryStats = (campaign) => {
    if (campaign.campaignType !== 'email') return null;

    const stats = {
      total: 0,
      sent: 0,
      bounced: 0,
      invalid: 0,
      opened: 0,
      clicked: 0,
      pending: 0
    };

    if (campaign.deliveryAttempts && Array.isArray(campaign.deliveryAttempts)) {
      stats.total = campaign.deliveryAttempts.length;
      campaign.deliveryAttempts.forEach(attempt => {
        if (attempt.state === 'SUCCESS') {
          stats.sent++;
        } else if (attempt.state === 'ERROR') {
          const errorMsg = attempt.error?.message || '';
          if (errorMsg.toLowerCase().includes('bounce')) {
            stats.bounced++;
          } else {
            stats.invalid++;
          }
        } else if (attempt.state === 'PENDING' || attempt.state === 'PROCESSING') {
          stats.pending++;
        }
      });
    } else if (campaign.to && Array.isArray(campaign.to)) {
      stats.total = campaign.to.length;
      if (campaign.deliveryState === 'SUCCESS') {
        stats.sent = campaign.to.length;
      } else if (campaign.deliveryState === 'ERROR') {
        stats.invalid = campaign.to.length;
      } else {
        stats.pending = campaign.to.length;
      }
    }

    return stats;
  };

  // Get engagement metrics (Delivered %, Opened %, Clickthroughs %) for any campaign type
  const getEngagementMetrics = (campaign) => {
    const status = getNotificationStatus(campaign);
    if (status !== 'sent') return null;

    // Total recipients targeted
    let total = 0;
    let delivered = 0;

    if (campaign.campaignType === 'email') {
      if (campaign.deliveryAttempts && Array.isArray(campaign.deliveryAttempts)) {
        total = campaign.deliveryAttempts.length;
        delivered = campaign.deliveryAttempts.filter(a => a.state === 'SUCCESS').length;
      } else if (campaign.to && Array.isArray(campaign.to)) {
        total = campaign.to.length;
        delivered = campaign.deliveryState === 'SUCCESS' ? total : 0;
      }
    } else if (campaign.campaignType === 'push' || campaign.campaignType === 'whatsapp') {
      const recipients = campaign.targetRecipients || campaign.to || [];
      total = recipients.length;
      // For push/whatsapp, if status is 'sent' treat all as delivered unless analytics says otherwise
      delivered = campaign.analytics?.delivered ?? total;
    }

    const opens = campaign.analytics?.opens ?? campaign.analytics?.opened ?? campaign.tracking?.opens ?? null;
    const clicks = campaign.analytics?.clicks ?? campaign.analytics?.clicked ?? campaign.analytics?.clickthroughs ?? campaign.tracking?.clicks ?? null;
    const unsubscribes = campaign.analytics?.unsubscribes ?? null;
    const spamReports = campaign.analytics?.spamReports ?? null;

    const deliveredPct = total > 0 ? Math.round((delivered / total) * 100) : null;
    const openedPct = delivered > 0 && opens !== null ? Math.round((opens / delivered) * 100) : null;
    const clicksPct = delivered > 0 && clicks !== null ? Math.round((clicks / delivered) * 100) : null;
    const unsubPct = delivered > 0 && unsubscribes !== null ? Math.round((unsubscribes / delivered) * 100) : null;
    const spamPct = delivered > 0 && spamReports !== null ? Math.round((spamReports / delivered) * 100) : null;

    return { total, delivered, opens, clicks, unsubscribes, spamReports, deliveredPct, openedPct, clicksPct, unsubPct, spamPct };
  };

  // Get notification status
  const getNotificationStatus = (notification) => {
    if (notification.deliveryState === 'SUCCESS') return 'sent';
    if (notification.deliveryState === 'ERROR') return 'failed';
    if (notification.status === 'sent') return 'sent';
    if (notification.status === 'sending') return 'sending';
    if (notification.status === 'failed') return 'failed';
    if (notification.metadata?.sendNow) return 'sending';

    const now = new Date();
    const scheduledDate = notification.scheduledDateTime;
    if (scheduledDate && now < scheduledDate) return 'scheduled';
    if (scheduledDate && now >= scheduledDate) return 'sending';

    return 'draft';
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString();
  };

  // Format targeting criteria into a readable string
  const formatTargetingCriteria = (targetingType, criteria) => {
    if (!targetingType || targetingType === 'all_customers' || targetingType === 'not_selected') {
      return 'All Customers';
    }

    const parts = [];

    if (targetingType === 'customer_profile') {
      if (criteria?.birthMonths && criteria.birthMonths.length > 0) {
        parts.push(`Birth Month(s): ${criteria.birthMonths.join(', ')}`);
      }
      if (criteria?.postalCodes && criteria.postalCodes.length > 0) {
        parts.push(`Postal Code(s): ${criteria.postalCodes.join(', ')}`);
      }
      if (parts.length === 0) parts.push('Customer Profile (no specific filters)');
    } else if (targetingType === 'customer_transaction') {
      const typeLabel = criteria?.transactionType === 'greater_than' ? 'more than' : 'less than';
      if (criteria?.transactionCount != null) {
        parts.push(`Transactions: ${typeLabel} ${criteria.transactionCount}`);
      }
      if (criteria?.lastTransactionMonths) {
        parts.push(`In last ${criteria.lastTransactionMonths} month(s)`);
      }
      const amountLabel = criteria?.transactionAmountType === 'greater_than' ? 'above' : 'below';
      if (criteria?.transactionAmount) {
        parts.push(`Amount: ${amountLabel} $${criteria.transactionAmount}`);
      }
      if (parts.length === 0) parts.push('Transaction Based (no specific filters)');
    } else if (targetingType === 'customer_points') {
      if (criteria?.memberTiers && criteria.memberTiers.length > 0) {
        parts.push(`Tier(s): ${criteria.memberTiers.join(', ')}`);
      }
      const pointsLabel = criteria?.pointsType === 'greater_than' ? 'more than' : 'less than';
      if (criteria?.pointsAmount != null) {
        parts.push(`Points: ${pointsLabel} ${criteria.pointsAmount}`);
      }
      if (parts.length === 0) parts.push('Points/Loyalty Based (no specific filters)');
    } else if (targetingType === 'customer_appointment') {
      if (criteria?.appointmentTimeValue && criteria?.appointmentTimeUnit) {
        parts.push(`Appointment within ${criteria.appointmentTimeValue} ${criteria.appointmentTimeUnit}`);
      }
      if (criteria?.appointmentService) {
        parts.push(`Service: ${criteria.appointmentService}`);
      }
      if (criteria?.appointmentStaff) {
        parts.push(`Staff: ${criteria.appointmentStaff}`);
      }
      if (criteria?.appointmentStatus && Array.isArray(criteria.appointmentStatus) && criteria.appointmentStatus.length > 0) {
        parts.push(`Status: ${criteria.appointmentStatus.join(', ')}`);
      }
      if (parts.length === 0) parts.push('Appointment Based (no specific filters)');
    } else if (targetingType === 'specific_customer') {
      const count = criteria?.specificCustomers?.length || 0;
      parts.push(`Specific Customers (${count} selected)`);
    } else {
      parts.push(targetingType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
    }

    return parts.join(' | ');
  };

  // Helper function to check if campaign can be edited (not sent)
  const canEditCampaign = (notification) => {
    const status = getNotificationStatus(notification);
    return status !== 'sent' && status !== 'sending';
  };

  // Helper function to check if campaign can show recipients (successfully sent)
  const canViewRecipients = (notification) => {
    const status = getNotificationStatus(notification);
    const successfulEmails = getSuccessfullyDeliveredEmails(notification);

    if (notification.campaignType === 'email') {
      return status === 'sent' && successfulEmails.length > 0;
    } else {
      return status === 'sent' && (notification.targetRecipients?.length > 0);
    }
  };

  // Get recipient count - shows only successfully delivered
  const getRecipientCount = (campaign) => {
    const successfulEmails = getSuccessfullyDeliveredEmails(campaign);
    return successfulEmails.length;
  };

  // Apply filters
  const filteredNotifications = notifications.filter(n => {
    if (filterChannel !== 'all' && n.campaignType !== filterChannel) return false;
    if (filterDateFrom || filterDateTo) {
      const sentDate = n.scheduledDateTime instanceof Date ? n.scheduledDateTime : new Date(n.scheduledDateTime);
      if (filterDateFrom && sentDate < new Date(filterDateFrom)) return false;
      if (filterDateTo) {
        const toEnd = new Date(filterDateTo);
        toEnd.setHours(23, 59, 59, 999);
        if (sentDate > toEnd) return false;
      }
    }
    return true;
  });

  const hasActiveFilters = filterChannel !== 'all' || filterDateFrom || filterDateTo;

  // ── Campaign Performance ────────────────────────────────────────────────────
  const [perfPeriod, setPerfPeriod] = useState('Last 12 Months');
  const [perfChannel, setPerfChannel] = useState('all');

  const perfPeriodOptions = ['Last 4 Weeks', 'Last 3 Months', 'Last 6 Months', 'Last 12 Months'];

  // Returns sent campaigns filtered by channel + period
  const sentCampaigns = useMemo(() => {
    const now = new Date();
    let startDate = new Date();
    switch (perfPeriod) {
      case 'Last 4 Weeks':   startDate.setDate(now.getDate() - 28); break;
      case 'Last 3 Months':  startDate.setMonth(now.getMonth() - 3); break;
      case 'Last 6 Months':  startDate.setMonth(now.getMonth() - 6); break;
      default:               startDate.setMonth(now.getMonth() - 12);
    }
    return notifications.filter(n => {
      if (getNotificationStatus(n) !== 'sent') return false;
      if (perfChannel !== 'all' && n.campaignType !== perfChannel) return false;
      const d = n.scheduledDateTime instanceof Date ? n.scheduledDateTime : new Date(n.scheduledDateTime);
      return d >= startDate && d <= now;
    });
  }, [notifications, perfPeriod, perfChannel]);

  // Build month-bucketed trend data for the line/bar chart
  const trendData = useMemo(() => {
    const buckets = {};
    sentCampaigns.forEach(n => {
      const d = n.scheduledDateTime instanceof Date ? n.scheduledDateTime : new Date(n.scheduledDateTime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!buckets[key]) buckets[key] = { period: key, opens: 0, clicks: 0, unsubs: 0, delivered: 0 };
      const m = getEngagementMetrics(n);
      if (m) {
        buckets[key].delivered += m.delivered || 0;
        buckets[key].opens += m.opens || 0;
        buckets[key].clicks += m.clicks || 0;
        buckets[key].unsubs += m.unsubscribes || 0;
      }
    });
    return Object.values(buckets).sort((a, b) => a.period.localeCompare(b.period));
  }, [sentCampaigns]);

  // Top campaigns ranked by opens for bar charts
  const topByOpens = useMemo(() => (
    sentCampaigns
      .map(n => ({ name: n.subject || n.title || 'Untitled', metrics: getEngagementMetrics(n) }))
      .filter(c => c.metrics && c.metrics.opens !== null)
      .sort((a, b) => b.metrics.opens - a.metrics.opens)
      .slice(0, 8)
  ), [sentCampaigns]);

  const topByClicks = useMemo(() => (
    sentCampaigns
      .map(n => ({ name: n.subject || n.title || 'Untitled', metrics: getEngagementMetrics(n) }))
      .filter(c => c.metrics && c.metrics.clicks !== null)
      .sort((a, b) => b.metrics.clicks - a.metrics.clicks)
      .slice(0, 8)
  ), [sentCampaigns]);

  const topByUnsubs = useMemo(() => (
    sentCampaigns
      .map(n => ({ name: n.subject || n.title || 'Untitled', metrics: getEngagementMetrics(n) }))
      .filter(c => c.metrics && c.metrics.unsubscribes !== null)
      .sort((a, b) => b.metrics.unsubscribes - a.metrics.unsubscribes)
      .slice(0, 8)
  ), [sentCampaigns]);

  // Aggregate totals for KPI tiles
  const perfTotals = useMemo(() => {
    let delivered = 0, opens = 0, clicks = 0, unsubs = 0;
    sentCampaigns.forEach(n => {
      const m = getEngagementMetrics(n);
      if (m) {
        delivered += m.delivered || 0;
        opens += m.opens || 0;
        clicks += m.clicks || 0;
        unsubs += m.unsubscribes || 0;
      }
    });
    return {
      campaigns: sentCampaigns.length,
      delivered,
      openRate: delivered > 0 ? Math.round((opens / delivered) * 100) : null,
      clickRate: delivered > 0 ? Math.round((clicks / delivered) * 100) : null,
      unsubRate: delivered > 0 ? Math.round((unsubs / delivered) * 100) : null,
    };
  }, [sentCampaigns]);

  // Trend line chart (multi-series SVG)
  const CampaignTrendChart = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div className="analytics-chart-card line-chart-full-width">
          <div className="chart-header"><h3>Engagement Over Time</h3></div>
          <div className="no-chart-data">No sent campaigns in this period</div>
        </div>
      );
    }

    const W = 1600, H = 300, PAD_L = 50, PAD_B = 40, PAD_T = 20, PAD_R = 20;
    const chartW = W - PAD_L - PAD_R;
    const chartH = H - PAD_T - PAD_B;

    const allVals = data.flatMap(d => [d.opens, d.clicks, d.unsubs]);
    const maxVal = Math.max(...allVals, 1);

    const toX = (i) => PAD_L + (i / Math.max(data.length - 1, 1)) * chartW;
    const toY = (v) => PAD_T + chartH - (v / maxVal) * chartH;

    const series = [
      { key: 'opens',  color: '#0070c9', label: 'Opened' },
      { key: 'clicks', color: '#00d4aa', label: 'Clickthroughs' },
      { key: 'unsubs', color: '#ff6b35', label: 'Unsubscribed' },
    ];

    const makePath = (key) =>
      data.map((d, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(d[key])}`).join(' ');

    return (
      <div className="analytics-chart-card line-chart-full-width">
        <div className="chart-header">
          <h3>Engagement Over Time</h3>
          <div style={{ display: 'flex', gap: 16 }}>
            {series.map(s => (
              <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#666' }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        <div className="line-chart-container">
          <svg width="100%" height="300" viewBox={`0 0 ${W} ${H}`} className="line-chart-svg" preserveAspectRatio="xMidYMid meet">
            {/* Grid */}
            {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
              const y = PAD_T + chartH - r * chartH;
              return (
                <g key={i}>
                  <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#f0f0f0" strokeWidth="1" />
                  <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="11" fill="#666">
                    {Math.round(r * maxVal)}
                  </text>
                </g>
              );
            })}
            {/* Series lines */}
            {series.map(s => (
              <path key={s.key} d={makePath(s.key)} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            ))}
            {/* Dots */}
            {series.map(s =>
              data.map((d, i) => (
                <circle key={`${s.key}-${i}`} cx={toX(i)} cy={toY(d[s.key])} r="4" fill={s.color}>
                  <title>{`${data[i].period} — ${s.label}: ${d[s.key]}`}</title>
                </circle>
              ))
            )}
            {/* X labels */}
            {data.map((d, i) => (
              <text key={i} x={toX(i)} y={H - 6} textAnchor="middle" fontSize="11" fill="#666">
                {d.period.substring(2)} {/* YY-MM */}
              </text>
            ))}
          </svg>
        </div>
        <div className="chart-summary">
          <div className="summary-item"><span className="summary-label">Campaigns sent:</span><span className="summary-value">{sentCampaigns.length}</span></div>
          <div className="summary-item"><span className="summary-label">Total Delivered:</span><span className="summary-value">{perfTotals.delivered}</span></div>
          <div className="summary-item"><span className="summary-label">Avg Open Rate:</span><span className="summary-value">{perfTotals.openRate !== null ? `${perfTotals.openRate}%` : '—'}</span></div>
        </div>
      </div>
    );
  };

  // Horizontal bar chart (mirrors AnalyticsChart in AnalyticsDashboardTab)
  const CampaignBarChart = ({ items, valueKey, label, color }) => {
    const maxVal = Math.max(...items.map(c => c.metrics?.[valueKey] ?? 0), 1);
    return (
      <div className="analytics-chart-card">
        <div className="chart-header"><h3>{label}</h3></div>
        {items.length === 0 ? (
          <div className="no-chart-data">No data available</div>
        ) : (
          <div className="analytics-chart-content">
            {items.map((c, i) => {
              const val = c.metrics?.[valueKey] ?? 0;
              const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
              const rate = c.metrics?.delivered > 0 ? Math.round((val / c.metrics.delivered) * 100) : null;
              return (
                <div key={i} className="analytics-chart-item">
                  <div className="chart-item-info">
                    <div className="chart-label" title={c.name} style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    {rate !== null && <div className="chart-subtitle">{rate}% rate</div>}
                  </div>
                  <div className="chart-value">{val}</div>
                  <div className="chart-bar-container">
                    <div className="analytics-chart-bar" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };
  // ── end Campaign Performance helpers ───────────────────────────────────────

  if (loading) {
    return <div className="loading">Loading campaigns...</div>;
  }

  return (
    <div className="notifications-editor">
      <h2>Campaign History & Management</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* ── Campaign Performance Section ────────────────────────────────── */}
      <div className="campaign-performance-section">
        <div className="analytics-overview" style={{ marginBottom: 0 }}>
          <h3>Campaign Performance</h3>
          <p>Engagement metrics across your sent campaigns</p>
        </div>

        {/* Performance controls */}
        <div className="analytics-controls" style={{ marginTop: 16, marginBottom: 20 }}>
          <div className="analysis-period-control">
            <label>Channel:</label>
            <select
              value={perfChannel}
              onChange={e => setPerfChannel(e.target.value)}
              className="filter-select analysis-period-select"
            >
              <option value="all">All Channels</option>
              <option value="email">Email</option>
              <option value="push">Push Notification</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div className="analysis-period-control" style={{ marginLeft: 20 }}>
            <label>Period:</label>
            <select
              value={perfPeriod}
              onChange={e => setPerfPeriod(e.target.value)}
              className="filter-select analysis-period-select"
            >
              {perfPeriodOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="analytics-kpi-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Campaigns Sent', value: perfTotals.campaigns, suffix: '' },
            { label: 'Total Delivered', value: perfTotals.delivered, suffix: '' },
            { label: 'Open Rate', value: perfTotals.openRate, suffix: '%' },
            { label: 'Click Rate', value: perfTotals.clickRate, suffix: '%' },
            { label: 'Unsub Rate', value: perfTotals.unsubRate, suffix: '%' },
          ].map(({ label, value, suffix }) => (
            <div key={label} className="analytics-chart-card" style={{ padding: '16px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#0070c9' }}>
                {value !== null && value !== undefined ? `${value}${suffix}` : '—'}
              </div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Trend chart — full width */}
        <div className="revenue-period-section" style={{ marginBottom: 24 }}>
          <CampaignTrendChart data={trendData} />
        </div>

        {/* Bar charts grid */}
        <div className="analytics-grid">
          <CampaignBarChart items={topByOpens}  valueKey="opens"        label="Top Campaigns by Opens"         color="#0070c9" />
          <CampaignBarChart items={topByClicks} valueKey="clicks"       label="Top Campaigns by Clickthroughs" color="#00d4aa" />
          <CampaignBarChart items={topByUnsubs} valueKey="unsubscribes" label="Top Campaigns by Unsubscribes"  color="#ff6b35" />
        </div>
      </div>
      {/* ── end Campaign Performance ─────────────────────────────────────── */}

      {/* Filters */}
      <div className="campaign-filters">
        <div className="campaign-filter-group">
          <label className="campaign-filter-label">Channel</label>
          <select
            className="campaign-filter-select"
            value={filterChannel}
            onChange={e => setFilterChannel(e.target.value)}
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="push">Push Notification</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>

        <div className="campaign-filter-group">
          <label className="campaign-filter-label">Sent From</label>
          <input
            type="date"
            className="campaign-filter-date"
            value={filterDateFrom}
            onChange={e => setFilterDateFrom(e.target.value)}
          />
        </div>

        <div className="campaign-filter-group">
          <label className="campaign-filter-label">Sent To</label>
          <input
            type="date"
            className="campaign-filter-date"
            value={filterDateTo}
            onChange={e => setFilterDateTo(e.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button
            className="campaign-filter-clear"
            onClick={() => { setFilterChannel('all'); setFilterDateFrom(''); setFilterDateTo(''); }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="notifications-list-view">
        {notifications.length === 0 ? (
          <div className="no-notifications">
            <p>No actual campaigns have been created yet.</p>
            <div className="quick-actions">
              <p>Use the Email Campaigns, Push Notification Campaigns, or WhatsApp Campaigns tabs to create your first campaign.</p>
            </div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="no-notifications">
            <p>No campaigns match the selected filters.</p>
            <button className="campaign-filter-clear" onClick={() => { setFilterChannel('all'); setFilterDateFrom(''); setFilterDateTo(''); }}>
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="notifications-list">
            {filteredNotifications.map(notification => {
              return (
                <div
                  key={notification.id}
                  className={`notification-item ${getNotificationStatus(notification)}`}
                >
                  <div className="notification-content">
                    <div className="notification-header">
                      <h4>{notification.subject || notification.title || 'Untitled Campaign'}</h4>
                      <div className="notification-badges">
                        <span className={`status-badge ${getNotificationStatus(notification)}`}>
                          {getNotificationStatus(notification).toUpperCase()}
                        </span>
                        <span className="type-badge">
                          {notification.displayType}
                        </span>
                      </div>
                    </div>

                    <p className="notification-body">
                      {notification.body ?
                        (() => {
                          const plain = notification.body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                          return plain.length > 100 ? plain.substring(0, 100) + '...' : plain || 'No message content';
                        })() :
                        'No message content'
                      }
                    </p>

                    {/* Engagement metrics row — shown for sent campaigns */}
                  {(() => {
                    const m = getEngagementMetrics(notification);
                    if (!m) return null;
                    const fmt = (pct, raw) => {
                      if (pct === null) return <span className="metric-na">—</span>;
                      return <span className="metric-value">{pct}%{raw !== null && raw !== undefined ? <span className="metric-raw"> ({raw})</span> : null}</span>;
                    };
                    return (
                      <div className="campaign-metrics-row">
                        <div className="campaign-metric">
                          <span className="metric-label">Delivered</span>
                          {fmt(m.deliveredPct, m.delivered)}
                          {m.total > 0 && <span className="metric-total"> of {m.total}</span>}
                        </div>
                        <div className="campaign-metric">
                          <span className="metric-label">Opened</span>
                          {fmt(m.openedPct, m.opens)}
                        </div>
                        <div className="campaign-metric">
                          <span className="metric-label">Clickthroughs</span>
                          {fmt(m.clicksPct, m.clicks)}
                        </div>
                        <div className="campaign-metric campaign-metric--warning">
                          <span className="metric-label">Unsubscribed</span>
                          {fmt(m.unsubPct, m.unsubscribes)}
                        </div>
                        <div className="campaign-metric campaign-metric--danger">
                          <span className="metric-label">Spam Reports</span>
                          {fmt(m.spamPct, m.spamReports)}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="notification-details">
                      <div className="detail-item">
                        <strong>Created:</strong> {formatDateTime(notification.metadata?.timestamp)}
                      </div>
                      <div className="detail-item">
                        <strong>Targeting Criteria:</strong> {formatTargetingCriteria(notification.targetingType, notification.targetingCriteria)}
                      </div>
                      <div className="detail-item">
                        <strong>Schedule Type:</strong>{' '}
                        {notification.schedulingType === 'recurring' || notification.metadata?.schedulingType === 'recurring' ? (
                          <span className="schedule-type-recurring">
                            Recurring — {(() => {
                              const rec = notification.recurring || notification.metadata?.recurring;
                              if (!rec) return 'Recurring';
                              const freq = rec.frequency ? rec.frequency.charAt(0).toUpperCase() + rec.frequency.slice(1) : '';
                              let endInfo = '';
                              if (rec.endType === 'date' && rec.endDate) {
                                endInfo = ` until ${new Date(rec.endDate).toLocaleDateString()}`;
                              } else if (rec.endType === 'cycles' && rec.numberOfCycles) {
                                endInfo = ` for ${rec.numberOfCycles} cycle(s)`;
                              }
                              return `${freq}${endInfo}`;
                            })()}
                          </span>
                        ) : (
                          <span className="schedule-type-onetime">One-time</span>
                        )}
                      </div>
                      <div className="detail-item">
                        <strong>Scheduled:</strong> {formatDateTime(notification.scheduledDateTime)}
                      </div>
                    </div>
                  </div>

                  <div className="notification-actions">
                    {canEditCampaign(notification) && (
                      <button
                        className="edit-button"
                        onClick={() => handleEditCampaign(notification)}
                        disabled={saving}
                      >
                        Edit
                      </button>
                    )}

                    {getNotificationStatus(notification) === 'scheduled' && (
                      <button
                        className="send-button"
                        onClick={() => handleSendNow(notification.id, notification.campaignType)}
                        disabled={saving}
                      >
                        Send Now
                      </button>
                    )}

                    {/* Retry button for WhatsApp campaigns stuck in 'sending' (never actually sent) */}
                    {notification.campaignType === 'whatsapp' &&
                      getNotificationStatus(notification) === 'sending' &&
                      !notification.sentAt && (
                      <button
                        className="send-button"
                        onClick={() => handleSendNow(notification.id, notification.campaignType)}
                        disabled={saving}
                      >
                        Retry Send
                      </button>
                    )}

                    {canViewRecipients(notification) && (
                      <button
                        className="view-recipients-button"
                        onClick={() => handleViewRecipients(notification)}
                        disabled={saving}
                      >
                        View Recipients ({getRecipientCount(notification)})
                      </button>
                    )}

                    <button
                      className="remove-button"
                      onClick={() => handleDeleteCampaign(notification.id, notification.campaignType)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recipients Modal - Shows only successfully delivered emails */}
      {viewingRecipients && (
        <div className="recipients-modal-overlay" onClick={closeRecipientsModal}>
          <div className="recipients-modal" onClick={(e) => e.stopPropagation()}>
            <div className="recipients-modal-header">
              <h3>Successfully Delivered Recipients</h3>
              <button className="close-modal-button" onClick={closeRecipientsModal}>×</button>
            </div>
            <div className="recipients-modal-body">
              <div className="recipients-summary-table">
                <table className="recipients-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Type</th>
                      <th>Sent</th>
                      <th>Recipients</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{viewingRecipients.subject || viewingRecipients.title || 'Untitled'}</td>
                      <td>{viewingRecipients.displayType}</td>
                      <td>{formatDateTime(viewingRecipients.scheduledDateTime)}</td>
                      <td>{getRecipientCount(viewingRecipients)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="recipients-list-container">
                <h4>Recipient List ({getRecipientCount(viewingRecipients)})</h4>
                <div className="recipients-list-scroll">
                  <table className="recipients-table recipients-detail-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Email</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSuccessfullyDeliveredEmails(viewingRecipients).map((email, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{email}</td>
                          <td className="recipient-status-delivered">Delivered</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="recipients-modal-footer">
              <button className="close-button" onClick={closeRecipientsModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignsListingEditor;
